import { RelayablyPhoneComplianceProfile, RelayablyWorkspacePhoneNumber } from '@monorepo/relayably/workspaces'
import { buildRelayablyInternalUrl } from '../api/relayablyInternalApi'

const request = async <T>(path: string, sessionToken: string, options: RequestInit = {}): Promise<T> => {
  const response = await fetch(buildRelayablyInternalUrl(path), {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'x-relayably-client': 'relayably-app',
      Authorization: `Bearer ${sessionToken}`,
      ...(options.headers ?? {}),
    },
  })

  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null
    throw new Error(body?.error?.message ?? 'Phone number provisioning failed.')
  }

  return (await response.json()) as T
}

type AvailableResponse = {
  numbers: RelayablyWorkspacePhoneNumber[]
  nextPageToken: string | null
  hasMore: boolean
}
type CurrentResponse = { number: RelayablyWorkspacePhoneNumber | null }
type AssignResponse =
  | {
      complianceProfile?: RelayablyPhoneComplianceProfile | null
      message?: string
      number: RelayablyWorkspacePhoneNumber
      outcome: 'assigned'
    }
  | {
      complianceProfile: RelayablyPhoneComplianceProfile
      message: string
      number: RelayablyWorkspacePhoneNumber
      outcome: 'pending_review'
    }
type ComplianceRequirementsResponse = {
  country: string
  endUserType: 'individual' | 'business'
  fields: Array<{ key: string; label: string }>
  hasRequirements: boolean
  kind: 'mobile' | 'local' | 'tollfree'
  missingDocuments: string[]
  missingFields: string[]
  profile: RelayablyPhoneComplianceProfile
  regulationLabel: string
  regulationSid: string
  requiredDocuments: Array<{
    acceptedDocuments: Array<{ fields: string[]; type: string }>
    label: string
    requirementName: string
  }>
  twilioNumberType: string
}
type ComplianceSaveResponse = {
  missingDocuments: string[]
  missingFields: string[]
  profile: RelayablyPhoneComplianceProfile
  requiresCompliance: boolean
}

export const relayablyPhoneNumberApi = {
  async getAvailableNumbers(
    sessionToken: string,
    params: {
      country: string
      kind: 'mobile' | 'local' | 'tollfree'
      region?: string
      postalCode?: string
      query?: string
      pageToken?: string | null
      pageSize?: number
    }
  ) {
    return request<AvailableResponse>(
      `/internal/numbers/available?country=${encodeURIComponent(params.country)}&kind=${encodeURIComponent(params.kind)}&region=${encodeURIComponent(params.region ?? '')}&postalCode=${encodeURIComponent(params.postalCode ?? '')}&query=${encodeURIComponent(params.query ?? '')}&pageToken=${encodeURIComponent(params.pageToken ?? '')}&pageSize=${encodeURIComponent(String(params.pageSize ?? 12))}`,
      sessionToken
    )
  },

  async reserveNumber(sessionToken: string, number: RelayablyWorkspacePhoneNumber) {
    return request<CurrentResponse>('/internal/numbers/reserve', sessionToken, {
      method: 'POST',
      body: JSON.stringify(number),
    })
  },

  async assignNumber(sessionToken: string, number: RelayablyWorkspacePhoneNumber) {
    return request<AssignResponse>('/internal/numbers/assign', sessionToken, {
      method: 'POST',
      body: JSON.stringify(number),
    })
  },

  async getComplianceRequirements(
    sessionToken: string,
    params: {
      country: string
      kind: 'mobile' | 'local' | 'tollfree'
      usageType?: 'personal' | 'professional' | 'business'
    }
  ) {
    return request<ComplianceRequirementsResponse>(
      `/internal/numbers/compliance?country=${encodeURIComponent(params.country)}&kind=${encodeURIComponent(params.kind)}&usageType=${encodeURIComponent(params.usageType ?? '')}`,
      sessionToken
    )
  },

  async saveComplianceProfile(
    sessionToken: string,
    payload: {
      country: string
      kind: 'mobile' | 'local' | 'tollfree'
      usageType?: 'personal' | 'professional' | 'business'
      profile: Partial<RelayablyPhoneComplianceProfile>
    }
  ) {
    return request<ComplianceSaveResponse>('/internal/numbers/compliance', sessionToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async uploadComplianceDocument(
    sessionToken: string,
    payload: {
      country: string
      kind: 'mobile' | 'local' | 'tollfree'
      usageType?: 'personal' | 'professional' | 'business'
      requirementName: string
      documentType: string
      fileName: string
      mimeType?: string
      base64: string
      label?: string
    }
  ) {
    return request<{
      document: RelayablyPhoneComplianceProfile['documents'][number]
      missingDocuments: string[]
      missingFields: string[]
      profile: RelayablyPhoneComplianceProfile
      requiresCompliance: boolean
    }>('/internal/numbers/compliance/documents', sessionToken, {
      method: 'POST',
      body: JSON.stringify(payload),
    })
  },

  async deleteComplianceDocument(
    sessionToken: string,
    params: {
      country: string
      kind: 'mobile' | 'local' | 'tollfree'
      usageType?: 'personal' | 'professional' | 'business'
      documentId: string
    }
  ) {
    return request<{
      deleted: boolean
      missingDocuments: string[]
      missingFields: string[]
      profile: RelayablyPhoneComplianceProfile
      requiresCompliance: boolean
    }>(
      `/internal/numbers/compliance/documents/${encodeURIComponent(params.documentId)}?country=${encodeURIComponent(params.country)}&kind=${encodeURIComponent(params.kind)}&usageType=${encodeURIComponent(params.usageType ?? '')}`,
      sessionToken,
      {
        method: 'DELETE',
      }
    )
  },

  async getCurrentNumber(sessionToken: string) {
    return request<CurrentResponse>('/internal/numbers/current', sessionToken)
  },

  async releaseCurrentNumber(sessionToken: string) {
    return request<CurrentResponse>('/internal/numbers/current', sessionToken, {
      method: 'DELETE',
    })
  },
}
